<template>
  <div class="w-100 top-bar">
    <top-bar-subinstance-bar
      :fetching="fetching"
      :instance="instance"
      :key="instance.id"
      v-on="$listeners"
    />
    <template v-if="!fetching">
      <div class="px-6">
        <v-container
          class="top-bar__container px-0"
          :class="{
            'mt-8': !$vuetify.breakpoint.lgAndDown,
            'mt-6': $vuetify.breakpoint.lg,
            'mt-4': $vuetify.breakpoint.mdAndDown,
          }"
          v-if="instance.info"
        >
          <v-row
            class="justify-space-between mx-0 mb-6 align-start"
            :class="$vuetify.breakpoint.mdAndDown ? 'flex-column' : 'flex-nowrap'"
          >
            <top-bar-title
              class="top-bar-title-col"
              :fetching="fetching"
              :instance="instance"
              :key="instance.id"
              v-on="$listeners"
              @dns-propagation-in-progress="isDnsPropagationInProgress = true"
            />

            <div
              class="top-bar-title__box top-bar-actions d-flex justify-start justify-lg-end align-center flex-shrink-0"
              :class="$vuetify.breakpoint.smAndDown ? 'flex-wrap' : ''"
            >
              <div v-if="$vuetify.breakpoint.smAndDown" class="d-flex mt-4">
                <share-instance-modal v-if="instance.sharedAccess.length">
                  <template v-slot="{ attrs, on }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      class="align-self-center mr-6"
                    >
                      <shares-label-big
                        :instance="instance"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </div>
                  </template>
                </share-instance-modal>

                <router-link
                  class="d-flex align-center mr-3"
                  :to="`/hosting/summary?id=${this.instance.hostingId}`"
                >
                  <span class="p-1 info--text d-flex align-center">
                    <v-icon size="16" class="mr-2" color="info"
                      >$externallink</v-icon
                    >
                    {{ $t("general.hosting") }}
                  </span>
                </router-link>
              </div>
              <div
                class="d-flex justify-start justify-lg-end align-center"
                :class="$vuetify.breakpoint.mdAndDown ? 'mt-4' : ''"
              >
                <template v-if="fetching">
                  <base-icon-button-skeleton
                    v-for="index in $vuetify.breakpoint.lgAndDown ? 2 : 5"
                    :key="index"
                  />
                </template>
                <template v-else>
                  <share-instance-modal
                    :instance="instance"
                    v-if="
                      instance.hasManageSharedAccessPrivilege()
                    "
                  >
                    <template v-slot:default="{ attrs, on }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="align-self-center mr-6"
                        :class="$vuetify.breakpoint.smAndDown ? 'd-none' : ''"
                        v-if="instance.sharedAccess.length"
                      >
                        <shares-label-big
                          :instance="instance"
                          icon="$shareIcon"
                          color="info"
                          class="link-text"
                          :text="$t('button.share')"
                          :class="$vuetify.breakpoint.smAndDown ? 'ml-3' : ''"
                          :small="$vuetify.breakpoint.smAndDown"
                        />
                      </div>
                    </template>
                  </share-instance-modal>
                  <base-icon-button
                    v-if="
                      isCollaborateEnabled &&
                      instance.hasCollaborationPrivilege()
                    "
                    :disabled="!instance.isActive"
                    @click.native="getCollaborationLink(instance.id)"
                    class="top-bar__btn"
                    icon="$collaborate"
                    :text="$t('button.collaborate')"
                    :colorDarken="true"
                  />
                  <base-icon-button
                    :disabled="!instance.isActive"
                    @click.native="isOpenPreviewSiteModal = true"
                    class="top-bar__btn"
                    :fetching="fetching"
                    icon="$previewDetails"
                    :text="$t('button.preview')"
                    v-if="showPreviewSiteButton && isCollaborateEnabled"
                  />
                  <template
                    v-if="
                      instance.subinstanceLabel !== 'staging' &&
                      instance.hasCreateStagingPrivilege() &&
                      !instance.hasStaging() &&
                      isCollaborateEnabled
                    "
                  >
                    <v-tooltip
                      transition="custom-tooltip"
                      open-delay="150"
                      bottom
                      nudge-right="4px"
                      :disabled="instance.subInstances.length < 2"
                      z-index="99"
                      v-if="!state || state == 'no_staging'"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <div v-on="on" v-bind="attrs">
                          <base-icon-button
                            icon="$wrench"
                            @click.native="
                              $emit('action-button-staging', instance)
                            "
                            :disabled="
                              instance.subInstances.length >= 2 ||
                              !instance.isActive
                            "
                            :class="$vuetify.breakpoint.smAndDown ? 'ml-3' : ''"
                            :text="$t('button.staging')"
                            :fetching="fetching"
                            class="top-bar__btn"
                            v-if="instance.hasCreateStagingPrivilege()"
                          />
                        </div>
                      </template>

                      <span>
                        {{ $t("tooltip.topBar.staging.create.inProgress") }}
                      </span>
                    </v-tooltip>

                    <v-menu
                      transition="custom-tooltip"
                      open-delay="150"
                      bottom
                      z-index="99"
                      offset-overflow
                      v-else-if="state && state != 'no_staging'"
                      open-on-hover
                      :close-on-content-click="false"
                      nudge-left="110px"
                      min-width="272px"
                      nudge-bottom="64px"
                      origin="top center"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <div v-on="on" v-bind="attrs">
                          <template v-if="state === 'in_progress'">
                            <base-dropdown-button
                              :loading="true"
                              :disabled="true"
                              :text="$t('button.clone')"
                              :fetching="fetching"
                              class="top-bar__btn"
                            />
                          </template>
                          <template v-else>
                            <base-dropdown-button
                              :icon="'$xcircle'"
                              color="error"
                              :text="$t('message.status.staging.failed')"
                              :fetching="fetching"
                              class="top-bar__btn"
                            />
                          </template>
                        </div>
                      </template>
                      <div class="px-4 py-4">
                        <template v-if="state === 'in_progress'">
                          <p class="p-4 text-center mb-2">
                            {{ $t("tooltip.topBar.staging.create.clone") }}
                          </p>
                          <div class="d-flex justify-center">
                            <v-btn
                              elevation="0"
                              color="error"
                              x-small
                              class="px-2"
                              max-height="24px"
                            >
                              <span class="p-5 font-weight-600">
                                {{ $t("button.cancelClone") }}
                              </span>
                            </v-btn>
                          </div>
                        </template>
                        <template v-else-if="state === 'error'">
                          <p class="p-4 text-center mb-2">
                            <i18next
                              :translation="
                                $t('tooltip.topBar.staging.create.error.plain')
                              "
                            >
                              <template #highlighted>
                                <strong>{{
                                  $t(
                                    "tooltip.topBar.staging.create.error.highlighted"
                                  )
                                }}</strong>
                              </template>
                            </i18next>
                          </p>
                          <div class="d-flex justify-center">
                            <v-btn
                              elevation="0"
                              color="primary"
                              x-small
                              class="mr-2 px-2"
                              max-height="24px"
                              @click="
                                $emit('action-button-retry-staging', instance)
                              "
                            >
                              <span class="p-5 font-weight-600">{{
                                $t("button.try")
                              }}</span>
                            </v-btn>
                            <v-btn
                              elevation="0"
                              color="error"
                              max-height="24px"
                              x-small
                              class="px-2"
                              @click="
                                $emit('action-button-cancel-staging', instance)
                              "
                            >
                              <span class="p-5 font-weight-600">{{
                                $t("button.cancel")
                              }}</span>
                            </v-btn>
                          </div>
                        </template>
                      </div>
                    </v-menu>
                  </template>
                  <base-icon-button
                    v-if="
                      instance.subinstanceLabel !== 'staging' &&
                      instance.hasPushToStagingPrivilege() &&
                      instance.hasActiveStaging() &&
                      isCollaborateEnabled
                    "
                    :disabled="!instance.isActive"
                    :loading="
                      loadingPushToStaging || instance.hasActionInProgress()
                    "
                    @click.native="openPushToStagingModal"
                    class="primary-btn top-bar__btn"
                    :text="$t('button.pushToStaging')"
                    icon="$copy"
                    :fetching="fetching"
                  />
                  <base-icon-button
                    v-else-if="
                      this.instance.subinstanceLabel === 'staging' &&
                      instance.hasPushToLivePrivilege() &&
                      isCollaborateEnabled
                    "
                    :disabled="!instance.isActive"
                    :loading="
                      loadingPushToLive || instance.hasActionInProgress()
                    "
                    @click.native="openPushToLiveModal"
                    class="primary-btn top-bar__btn"
                    :text="$t('button.pushToLive')"
                    icon="$send"
                    :fetching="fetching"
                  />
                  <base-icon-button
                    :disabled="!instance.isActive"
                    @click.native="$emit('action-button-clearcache', instance)"
                    :fetching="fetching"
                    icon="$restore"
                    :text="$t('button.cache')"
                    v-if="
                      instance.hasClearCachePrivilege() &&
                      isCollaborateEnabled
                    "
                  />

                  <v-menu
                    v-if="isCollaborateEnabled"
                    elevation="4"
                    offset-overflow
                    nudge-bottom="16px"
                    offset-y
                    left
                    bottom
                    :min-width="'220px'"
                    :max-width="'290px'"
                    :close-on-content-click="false"
                    v-model="open"
                    @input="handleInput"
                    :transition="false"
                    :disabled="disabled"
                    :class="'instance-top-dropdown'"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <slot name="activator" v-bind="{ on, attrs }">
                        <v-btn
                          class="instance-item__more-button"
                          v-on="on"
                          v-bind="attrs"
                          icon
                          small
                        >
                          <v-icon>$morehorizontal</v-icon>
                        </v-btn>
                      </slot>
                    </template>
                    <div v-click-outside="() => hideMainDropdown">
                      <v-list class="" v-if="!menuOpen">
                        <v-list-item
                          class="instance-top-dropdown-item"
                          v-if="instance.hasManageSharedAccessPrivilege()"
                        >
                          <share-instance-modal :instance="instance">
                            <template v-slot:default="{ attrs, on }">
                              <div v-bind="attrs" v-on="on">
                                <base-dropdown-button
                                  :text="$t('button.share')"
                                  icon="$shareIcon"
                                  color="info"
                                  class=""
                                />
                              </div>
                            </template>
                          </share-instance-modal>
                        </v-list-item>
                        <v-list-item
                          class="instance-top-dropdown-item"
                          v-if="instance.hasCreateSsoLinkPrivilege()"
                        >
                          <base-dropdown-button
                            :disabled="!instance.isActive"
                            :fetching="fetching"
                            @click.native="openWPLoginModal"
                            :text="$t('button.wpAdmin')"
                            icon="$wordpress"
                            color="info"
                            class=""
                            :loading="checkingWpLogin"
                          />
                        </v-list-item>
                        <v-list-item
                          class="instance-top-dropdown-item"
                          v-if="
                            !$vuetify.breakpoint.lgAndDown &&
                            instance.plan_settings.copy_instance_allowed &&
                            instance.hasCopyInstancePrivilege()
                          "
                        >
                          <base-dropdown-button
                            :disabled="!instance.isActive"
                            @click.native="
                              $emit('action-button-copy', instance)
                            "
                            class=""
                            :fetching="fetching"
                            icon="$copy"
                            :text="$t('button.copy')"
                          />
                        </v-list-item>
                        <v-list-item class="instance-top-dropdown-item">
                          <template :instance="instance">
                            <template
                              v-if="
                                instance.status.wordpress_available >
                                instance.status.wordpress
                              "
                            >
                              <base-dropdown-button
                                :disabled="!instance.isActive"
                                @click.native="openUpdateModal"
                                :fetching="fetching"
                                icon="$update"
                                :loading="
                                  checkingUpdates ||
                                  instance.hasUpdateInProgress()
                                "
                                class=""
                                :text="$t('button.update')"
                                v-if="
                                  instance.hasWordPressUpdatePrivilege()
                                "
                              />
                            </template>

                            <template v-else>
                              <v-tooltip
                                transition="custom-tooltip"
                                open-delay="150"
                                bottom
                                nudge-bottom="0px"
                                v-if="
                                  instance.hasWordPressUpdatePrivilege()
                                "
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <div v-on="on" v-bind="attrs">
                                    <base-dropdown-button
                                      @click.native="openUpdateModal"
                                      :fetching="fetching"
                                      icon="$update"
                                      :disabled="true"
                                      :text="$t('button.update')"
                                      class=""
                                    />
                                  </div>
                                </template>
                                <span>{{ $t("tooltip.topBar.update") }}</span>
                              </v-tooltip>
                            </template>
                          </template>
                        </v-list-item>
                        <v-divider
                          v-if="instance.hasDeleteInstancePrivilege()"
                        />
                        <v-list-item
                          v-if="instance.hasDeleteInstancePrivilege()"
                          class="instance-top-dropdown-item"
                        >
                          <base-dropdown-button
                            :fetching="fetching"
                            icon="$xcircle"
                            :text="$t('button.delete')"
                            :color="'error'"
                            class=""
                            @click.native="
                              $emit('action-button-delete', instance)
                            "
                          />
                          <instance-item-menu
                            v-if="false"
                            :inInstance="true"
                            :id="this.instance.id"
                            :instance="this.instance"
                            v-on="$listeners"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <base-dropdown-button
                                :on="on"
                                :fetching="fetching"
                                :attrs="attrs"
                                icon="$morehorizontal"
                                :text="
                                  !$vuetify.breakpoint.lgAndDown
                                    ? $t('button.more')
                                    : ''
                                "
                              />
                            </template>
                          </instance-item-menu>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-menu>
                  <div v-else class="d-flex">
                    <share-instance-modal
                      :instance="instance"
                      v-if="instance.hasManageSharedAccessPrivilege()"
                    >
                      <template v-slot:default="{ attrs, on }">
                        <div v-bind="attrs" v-on="on">
                          <base-icon-button
                            :text="$t('button.share')"
                            icon="$shareIcon"
                            color="info"
                            class="top-bar__btn"
                            :small="
                              $vuetify.breakpoint.smAndDown ? true : false
                            "
                          />
                        </div>
                      </template>
                    </share-instance-modal>

                    <base-icon-button
                      :disabled="!instance.isActive"
                      :fetching="fetching"
                      @click.native="openWPLoginModal"
                      :text="$t('button.wpAdmin')"
                      icon="$wordpress"
                      color="info"
                      class="top-bar__btn"
                      :loading="checkingWpLogin"
                      :class="$vuetify.breakpoint.smAndDown ? 'ml-3' : ''"
                      :small="$vuetify.breakpoint.smAndDown ? true : false"
                      v-if="
                        instance.hasCreateSsoLinkPrivilege()
                      "
                    />
                    <base-icon-button
                      :disabled="!instance.isActive"
                      @click.native="isOpenPreviewSiteModal = true"
                      class=""
                      :fetching="fetching"
                      icon="$previewDetails"
                      :text="$t('button.preview')"
                      v-if="showPreviewSiteButton"
                    />

                    <template
                      v-if="
                        instance.subinstanceLabel !== 'staging' &&
                        instance.hasCreateStagingPrivilege() &&
                        !instance.hasStaging()
                      "
                    >
                      <v-tooltip
                        transition="custom-tooltip"
                        open-delay="150"
                        bottom
                        nudge-right="4px"
                        :disabled="instance.subInstances.length < 2"
                        z-index="99"
                        v-if="!state || state == 'no_staging'"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div v-on="on" v-bind="attrs">
                            <base-icon-button
                              icon="$wrench"
                              @click.native="
                                $emit('action-button-staging', instance)
                              "
                              :disabled="
                                instance.subInstances.length >= 2 ||
                                !instance.isActive
                              "
                              :class="
                                $vuetify.breakpoint.smAndDown ? 'ml-3' : ''
                              "
                              :text="$t('button.staging')"
                              :small="
                                $vuetify.breakpoint.smAndDown ? true : false
                              "
                              :fetching="fetching"
                              class="top-bar__btn"
                              v-if="instance.hasCreateStagingPrivilege()"
                            />
                          </div>
                        </template>

                        <span>
                          {{ $t("tooltip.topBar.staging.create.inProgress") }}
                        </span>
                      </v-tooltip>

                      <v-menu
                        transition="custom-tooltip"
                        open-delay="150"
                        bottom
                        z-index="99"
                        offset-overflow
                        v-else-if="state && state != 'no_staging'"
                        open-on-hover
                        :close-on-content-click="false"
                        nudge-left="110px"
                        min-width="272px"
                        nudge-bottom="64px"
                        origin="top center"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div v-on="on" v-bind="attrs">
                            <template v-if="state === 'in_progress'">
                              <base-icon-button
                                :loading="true"
                                :disabled="true"
                                :text="$t('button.clone')"
                                :fetching="fetching"
                                :small="
                                  $vuetify.breakpoint.lgAndDown ? true : false
                                "
                              />
                            </template>
                            <template v-else>
                              <base-icon-button
                                :icon="'$xcircle'"
                                color="error"
                                :text="$t('message.status.staging.failed')"
                                :small="
                                  $vuetify.breakpoint.smAndDown ? true : false
                                "
                                :fetching="fetching"
                              />
                            </template>
                          </div>
                        </template>
                        <div class="px-4 py-4">
                          <template v-if="state === 'in_progress'">
                            <p class="p-4 text-center mb-2">
                              {{ $t("tooltip.topBar.staging.create.clone") }}
                            </p>
                            <div class="d-flex justify-center">
                              <v-btn
                                elevation="0"
                                color="error"
                                x-small
                                class="px-2"
                                max-height="24px"
                              >
                                <span class="p-5 font-weight-600">
                                  {{ $t("button.cancelClone") }}
                                </span>
                              </v-btn>
                            </div>
                          </template>
                          <template v-else-if="state === 'error'">
                            <p class="p-4 text-center mb-2">
                              <i18next
                                :translation="
                                  $t(
                                    'tooltip.topBar.staging.create.error.plain'
                                  )
                                "
                              >
                                <template #highlighted>
                                  <strong>{{
                                    $t(
                                      "tooltip.topBar.staging.create.error.highlighted"
                                    )
                                  }}</strong>
                                </template>
                              </i18next>
                            </p>
                            <div class="d-flex justify-center">
                              <v-btn
                                elevation="0"
                                color="primary"
                                x-small
                                class="mr-2 px-2"
                                max-height="24px"
                                @click="
                                  $emit('action-button-retry-staging', instance)
                                "
                              >
                                <span class="p-5 font-weight-600">{{
                                  $t("button.try")
                                }}</span>
                              </v-btn>
                              <v-btn
                                elevation="0"
                                color="error"
                                max-height="24px"
                                x-small
                                class="px-2"
                                @click="
                                  $emit(
                                    'action-button-cancel-staging',
                                    instance
                                  )
                                "
                              >
                                <span class="p-5 font-weight-600">{{
                                  $t("button.cancel")
                                }}</span>
                              </v-btn>
                            </div>
                          </template>
                        </div>
                      </v-menu>
                    </template>

                    <base-icon-button
                      v-else-if="
                        instance.subinstanceLabel !== 'staging' &&
                        instance.hasPushToStagingPrivilege() &&
                        instance.hasActiveStaging()
                      "
                      :disabled="!instance.isActive"
                      :loading="
                        loadingPushToStaging || instance.hasActionInProgress()
                      "
                      @click.native="openPushToStagingModal"
                      class="primary-btn top-bar__btn"
                      :text="$t('button.pushToStaging')"
                      icon="$copy"
                      :fetching="fetching"
                    />

                    <base-icon-button
                      :disabled="!instance.isActive"
                      :loading="
                        loadingPushToLive || instance.hasActionInProgress()
                      "
                      @click.native="openPushToLiveModal"
                      class="primary-btn top-bar__btn"
                      :text="$t('button.pushToLive')"
                      icon="$send"
                      :fetching="fetching"
                      v-else-if="
                        this.instance.subinstanceLabel === 'staging' &&
                        instance.hasPushToLivePrivilege()
                      "
                    />

                    <base-icon-button
                      :disabled="!instance.isActive"
                      @click.native="$emit('action-button-copy', instance)"
                      class="top-bar__btn"
                      :fetching="fetching"
                      icon="$copy"
                      :text="$t('button.copy')"
                      :class="$vuetify.breakpoint.lgAndDown ? 'ml-3' : ''"
                      :small="$vuetify.breakpoint.lgAndDown ? true : false"
                      v-if="
                        !$vuetify.breakpoint.lgAndDown &&
                        instance.plan_settings.copy_instance_allowed &&
                        instance.hasCopyInstancePrivilege()
                      "
                    />

                    <template :instance="instance">
                      <template
                        v-if="
                          instance.status.wordpress_available >
                          instance.status.wordpress
                        "
                      >
                        <base-icon-button
                          :disabled="!instance.isActive"
                          @click.native="openUpdateModal"
                          :fetching="fetching"
                          icon="$update"
                          :loading="
                            checkingUpdates || instance.hasUpdateInProgress()
                          "
                          class="top-bar__btn"
                          :text="$t('button.update')"
                          :small="$vuetify.breakpoint.lgAndDown"
                          v-if="instance.hasWordPressUpdatePrivilege()"
                        />
                      </template>

                      <template v-else>
                        <v-tooltip
                          transition="custom-tooltip"
                          open-delay="150"
                          bottom
                          nudge-bottom="0px"
                          v-if="instance.hasWordPressUpdatePrivilege()"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div v-on="on" v-bind="attrs">
                              <base-icon-button
                                @click.native="openUpdateModal"
                                :fetching="fetching"
                                icon="$update"
                                :disabled="true"
                                :text="$t('button.update')"
                                class="top-bar__btn"
                                :small="$vuetify.breakpoint.lgAndDown"
                              />
                            </div>
                          </template>
                          <span>{{ $t("tooltip.topBar.update") }}</span>
                        </v-tooltip>
                      </template>
                    </template>

                    <base-icon-button
                      :disabled="!instance.isActive"
                      @click.native="
                        $emit('action-button-clearcache', instance)
                      "
                      :fetching="fetching"
                      icon="$restore"
                      :text="$t('button.cache')"
                      :class="$vuetify.breakpoint.lgAndDown ? 'ml-3' : ''"
                      :small="$vuetify.breakpoint.lgAndDown ? true : false"
                      v-if="instance.hasClearCachePrivilege()"
                    />

                    <base-icon-button
                      v-if="
                        instance.hasDeleteInstancePrivilege()
                      "
                      :fetching="fetching"
                      icon="$xcircle"
                      :text="$t('button.delete')"
                      :color="'error'"
                      class="top-bar__btn"
                      @click.native="$emit('action-button-delete', instance)"
                    />

                    <instance-item-menu
                      v-else-if="false"
                      :inInstance="true"
                      :id="this.instance.id"
                      :instance="this.instance"
                      v-on="$listeners"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <base-icon-button
                          :on="on"
                          :fetching="fetching"
                          :attrs="attrs"
                          icon="$morehorizontal"
                          :text="
                            !$vuetify.breakpoint.lgAndDown
                              ? $t('button.more')
                              : ''
                          "
                          :small="$vuetify.breakpoint.lgAndDown"
                          :class="$vuetify.breakpoint.lgAndDown ? 'ml-3' : ''"
                        />
                      </template>
                    </instance-item-menu>
                  </div>
                </template>
              </div>
            </div>
          </v-row>

          <instance-top-bar-tabs
            v-if="instance.isActive"
            :instance="instance"
            :fetching="fetching"
            v-on="$listeners"
            @openModal="openedModal = true"
          />
        </v-container>
      </div>
    </template>
    <template v-else>
      <top-bar-skeleton />
    </template>
    <v-dialog
      transition="custom-dialog-transition"
      width="560px"
      v-model="openedModal"
    >
      <report-provider-tracking-code-modal
        :instance="instance"
        @modal-close="openedModal = false"
      />
    </v-dialog>
    <preview-site-modal
      :is-open.sync="isOpenPreviewSiteModal"
      :instance="instance"
      @close="isOpenPreviewSiteModal = false"
      @preview-site-generated="onPreviewSiteGenerated"
    />
  </div>
</template>

<script>
import TopBarTitle from "./TopBarTitle";
import InstanceTopBarTabs from "./InstanceTopBarTabs.vue";
import BaseDropdownButton from "../buttons/BaseDropdownButton.vue";
import BaseIconButton from "../buttons/BaseIconButton.vue";
import TopBarSubinstanceBar from "./TopBarSubinstanceBar.vue";
import BaseIconButtonSkeleton from "../buttons/BaseIconButtonSkeleton.vue";
import InstanceItemMenu from "../instances/InstanceItemMenu.vue";
import SharesLabelBig from "../labels/SharesLabelBig.vue";
import ShareInstanceModal from "../modal/ShareInstanceModal.vue";

import TopBarSkeleton from "./TopBarSkeleton.vue";

import {
  OpenWPLoginModalMixin,
  OpenUpdatesModalMixin,
  OpenPushToLiveModalMixin,
  OpenPushToStagingModalMixin,
} from "../../mixins/OpenModalAction";
import ReportProviderTrackingCodeModal from "../modal/ReportProviderTrackingCodeModal.vue";
import PreviewSiteModal from "../modal/PreviewSiteModal.vue";
import CollaborationLinkMixin from "../../mixins/CollaborationLinkMixin";

export default {
  components: {
    PreviewSiteModal,
    ReportProviderTrackingCodeModal,
    TopBarTitle,
    InstanceTopBarTabs,
    BaseIconButton,
    BaseDropdownButton,
    TopBarSubinstanceBar,
    BaseIconButtonSkeleton,
    TopBarSkeleton,
    InstanceItemMenu,
    SharesLabelBig,
    ShareInstanceModal,
  },
  mixins: [
    OpenWPLoginModalMixin,
    OpenUpdatesModalMixin,
    OpenPushToLiveModalMixin,
    OpenPushToStagingModalMixin,
    CollaborationLinkMixin,
  ],
  props: {
    instance: Object,
    fetching: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      searchField: "",
      openedModal: false,
      isOpenPreviewSiteModal: false,
      isDnsPropagationInProgress: false,
      open: false,
      menuOpen: "",
    };
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    showPreviewSiteButton() {
      if (this.isAppSingleServer) {
        return true;
      }
      return (
        this.instance.hasPreviewInstancePrivilege() &&
        this.$store.state.home.app.integrations.preview_site_provider_enabled &&
        this.isDnsPropagationInProgress
      );
    },
    state: function () {
      if (this.instance.subInstances.length >= 2) {
        return this.instance.subInstances[1].hasInstallStatus()
          ? this.instance.subInstances[1].hasInstallFailed()
            ? "error"
            : "in_progress"
          : false;
      } else {
        return "no_staging";
      }
    },
    wordpress: function () {
      return this.instance.status.wordpress;
    },
    woocommerce: function () {
      return this.instance.status.woocommerce;
    },
    visibleLabels: function () {
      return this.instance.labels.slice(0, 2);
    },
    hiddenLabelsNumber: function () {
      if (this.instance.labels.length > 2) {
        return this.instance.labels.length - 2;
      } else {
        return false;
      }
    },
    isCollaborateEnabled() {
      return this.$store.getters.isCollaborateEnabled;
      // return true;
    },
  },
  methods: {
    onPreviewSiteGenerated(payload) {
      this.$emit("preview-site-generated", payload);
    },
    hideMainDropdown: function () {
      this.open = false;
      this.$nextTick(() => {
        this.menuOpen = "";
      });
    },
    handleInput(value) {
      if (!value[0]) {
        this.menuOpen = "";
      }
    },
    handleBeforeUnload(e) {
      if (this.loadingPushToLive || this.loadingPushToStaging) {
        const message =
          "Action in progress. Leaving the page may cause the process to fail.";
        (e || window.event).returnValue = message;
        return message;
      }
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
};
</script>

<style lang="scss" scoped>
.v-tab {
  // text-transform: capitalize;
}

.back-button {
  .v-icon {
    color: #b0b0b0;
  }

  &:hover {
    .v-icon {
      color: map-get($primary, base) !important;
    }
  }
}

.top-bar {
  .top-bar__item {
    &:hover,
    &.v-tab--active {
      color: map-get($primary, darken1) !important;
    }
  }

  ::v-deep .top-bar-title-col {
    min-width: 0;
    flex: 1 1 auto;
    max-width: calc(100% - 380px);

    @media (max-width: map-get($grid-breakpoints, md)) {
      max-width: 100%;
    }
  }

  .top-bar-actions {
    flex-shrink: 0;
  }
}

.v-btn--vertical {
  padding-right: 12px;
}
</style>
